/**
 * HTTP客户端基础实现 - L1网络通信层
 * 提供基础的HTTP通信能力，支持Cookie和Token双模式认证
 */
import { ApiError } from './types.js';
import { createApiError, createErrorFromStatus, isRetryableError, isNetworkError, isTimeoutError, extractErrorMessage, } from './errors.js';
/**
 * HTTP客户端类
 * 核心职责：
 * 1. 基础HTTP通信
 * 2. 请求响应格式化
 * 3. 错误处理和重试
 * 4. Cookie和Token认证支持
 */
export class HttpClient {
    constructor(config = {}) {
        this.baseURL = config.baseURL || '/api';
        // A reasonable default timeout for most API calls.
        // Long-running tasks should provide a specific, longer timeout in their request options.
        this.timeout = config.timeout || 10000; // 10 seconds
        this.retries = config.retries || 3;
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            ...config.headers,
        };
    }
    /**
     * 基础请求方法
     * 支持超时、重试和错误处理
     */
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const { config } = this.buildRequestConfig(options);
        let lastError;
        let attempt = 0;
        while (attempt <= this.retries) {
            try {
                const response = await this.executeRequest(url, config);
                return await this.processResponse(response);
            }
            catch (error) {
                lastError = this.handleRequestError(error);
                // 最后一次尝试或错误不可重试，直接抛出
                if (attempt === this.retries || !isRetryableError(lastError)) {
                    throw lastError;
                }
                // 重试前等待
                await this.delay(this.getRetryDelay(attempt));
                attempt++;
            }
        }
        throw lastError;
    }
    /**
     * GET请求
     */
    async get(endpoint, config) {
        return this.request(endpoint, { ...config, method: 'GET' });
    }
    /**
     * POST请求
     */
    async post(endpoint, data, config) {
        return this.request(endpoint, {
            ...config,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    /**
     * PUT请求
     */
    async put(endpoint, data, config) {
        return this.request(endpoint, {
            ...config,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    /**
     * DELETE请求
     */
    async delete(endpoint, config) {
        return this.request(endpoint, { ...config, method: 'DELETE' });
    }
    /**
     * 流式请求方法
     * 用于处理SSE等流式响应
     */
    async requestStream(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        // MODIFIED: Capture the timeoutId along with the config.
        const { config, timeoutId } = this.buildRequestConfig(options);
        try {
            const response = await this.executeRequest(url, config);
            if (!response.ok) {
                // Clear the timeout because we have received a response, even if it's an error.
                clearTimeout(timeoutId);
                let errorData;
                try {
                    errorData = await response.json();
                }
                catch {
                    throw createErrorFromStatus(response.status);
                }
                const errorMessage = extractErrorMessage(errorData);
                throw createErrorFromStatus(response.status, errorMessage);
            }
            if (!response.body) {
                // Clear the timeout because we have a response, but it's unexpectedly empty.
                clearTimeout(timeoutId);
                throw createApiError('Response body is empty', 'EMPTY_BODY', response.status);
            }
            // SUCCESS: The stream is about to be returned. The timeout is no longer needed.
            clearTimeout(timeoutId);
            return { stream: response.body, error: null };
        }
        catch (error) {
            // The request failed (e.g., network error, or timeout already triggered).
            // The abort listener in buildRequestConfig will have already cleared the timeout.
            if (error instanceof ApiError) {
                throw error;
            }
            throw this.handleRequestError(error);
        }
    }
    /**
     * 构建请求配置
     */
    buildRequestConfig(options) {
        const timeout = options.timeout || this.timeout;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        const finalHeaders = {
            ...this.defaultHeaders,
            ...options.headers,
        };
        // If the body is FormData, the browser must set the Content-Type header itself.
        // Manually setting it to 'multipart/form-data' will cause a 'missing boundary' error.
        // Deleting it allows the browser to correctly set it with the appropriate boundary.
        if (options.body instanceof FormData) {
            delete finalHeaders['Content-Type'];
        }
        const config = {
            ...options,
            headers: finalHeaders,
            credentials: 'include',
            signal: controller.signal,
        };
        // When the request is aborted for any reason (timeout or otherwise), clear the timeout timer.
        config.signal?.addEventListener('abort', () => {
            clearTimeout(timeoutId);
        });
        return { config, timeoutId };
    }
    /**
     * 执行请求
     */
    async executeRequest(url, config) {
        try {
            const response = await fetch(url, config);
            return response;
        }
        catch (error) {
            // 网络错误或超时
            if (isNetworkError(error)) {
                throw createApiError('Network connection failed', 'NETWORK_ERROR', 0);
            }
            if (isTimeoutError(error)) {
                throw createApiError('Request timeout', 'TIMEOUT', 0);
            }
            throw createApiError('Request failed', 'REQUEST_FAILED', 0, error);
        }
    }
    /**
     * 处理响应
     */
    async processResponse(response) {
        // HTTP错误状态码
        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            }
            catch {
                // 无法解析响应体，使用默认错误信息
                throw createErrorFromStatus(response.status);
            }
            // 安全地提取错误信息
            const errorMessage = extractErrorMessage(errorData);
            throw createErrorFromStatus(response.status, errorMessage);
        }
        // 解析成功响应
        try {
            const data = await response.json();
            return data;
        }
        catch (error) {
            throw createApiError('Failed to parse response', 'PARSE_ERROR', response.status, error);
        }
    }
    /**
     * 处理请求错误
     */
    handleRequestError(error) {
        if (error instanceof ApiError) {
            return error;
        }
        const message = error instanceof Error ? error.message : 'Unknown error';
        return createApiError(message, 'UNKNOWN', 0, error);
    }
    /**
     * 获取重试延迟时间
     * 使用指数退避策略
     */
    getRetryDelay(attempt) {
        return Math.min(1000 * Math.pow(2, attempt), 10000); // 最大10秒
    }
    /**
     * 延迟函数
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /**
     * 更新默认请求头
     */
    setDefaultHeader(key, value) {
        this.defaultHeaders[key] = value;
    }
    /**
     * 移除默认请求头
     */
    removeDefaultHeader(key) {
        delete this.defaultHeaders[key];
    }
    /**
     * 获取基础URL
     */
    getBaseUrl() {
        return this.baseURL;
    }
    /**
     * 获取认证Token (不含 'Bearer ')
     */
    getAuthToken() {
        const authHeader = this.defaultHeaders['Authorization'];
        if (authHeader && authHeader.startsWith('Bearer ')) {
            return authHeader.substring(7);
        }
        return undefined;
    }
    getConfig() {
        return {
            baseURL: this.baseURL,
            timeout: this.timeout,
            retries: this.retries,
            headers: { ...this.defaultHeaders },
        };
    }
}
//# sourceMappingURL=client.js.map